import { useMemo, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { errorNotify } from '../../../utils/toast';
import { buildTripFilterPayload } from './TripsList';

const PDF_EXPORT = {
  request: (payload) => apiService.getTripListingPdf(payload),
  fileName: 'trip-listing.pdf',
  fileType: 'PDF',
};

const EXCEL_EXPORT = {
  request: (payload) => apiService.getTripListingExcel(payload),
  fileName: 'trip-listing.xlsx',
  fileType: 'Excel',
};

// Shared by both the standalone /trips-list page (TripsList.jsx's own header) and the
// /trips?tab=listing page (Trips.jsx's header, which embeds TripsList with hideHeader),
// so both download buttons use the same filters and response handling in both places.
const useTripListingExport = (searchValue, searchFilters, exportConfig) => {
  const [loading, setLoading] = useState(false);

  const hasActiveFilters = useMemo(
    () => Object.keys(buildTripFilterPayload(searchValue, searchFilters)).length > 0,
    [searchValue, searchFilters],
  );

  const handleExport = async () => {
    setLoading(true);
    try {
      const payload = buildTripFilterPayload(searchValue, searchFilters);
      const response = await exportConfig.request(payload);
      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url || responseData?.file || (typeof responseData === 'string' ? responseData : null);
      if (!filePath) {
        throw new Error(`${exportConfig.fileType} was generated but no file was returned`);
      }
      // The backend may return either a full URL (e.g. cloud storage) or a relative path.
      const backendBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');
      const fileUrl = /^https?:\/\//i.test(filePath)
        ? filePath
        : filePath.startsWith('/public') || filePath.startsWith('/uploads')
        ? `${backendBaseUrl}${filePath}`
        : `${process.env.REACT_APP_API_FILES_URL}${filePath.replace(/^\//, '')}`;

      if (exportConfig.fileType === 'PDF') {
        const openedWindow = window.open(fileUrl, '_blank');
        if (!openedWindow) {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.setAttribute('download', exportConfig.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      errorNotify(error?.message || error || `Failed to export trip listing ${exportConfig.fileType}`);
    } finally {
      setLoading(false);
    }
  };

  return { hasActiveFilters, loading, handleExport };
};

export const useTripListingPdfExport = (searchValue, searchFilters) =>
  useTripListingExport(searchValue, searchFilters, PDF_EXPORT);

export const useTripListingExcelExport = (searchValue, searchFilters) =>
  useTripListingExport(searchValue, searchFilters, EXCEL_EXPORT);
